import { PlusOutlined } from "@ant-design/icons";
import type { PanelAccent } from "./panelAccents";

/**
 * Общият „панел със секция" на модул Конфигурация — еталонът е одобреният
 * дизайн на страница Монитори: бяла карта с хедър (иконна значка, kicker,
 * заглавие + брояч-пилюла, toolbar вдясно) и ВЛОЖЕНА заоблена таблица с
 * отстъп от ръба, компактен неутрален хедър на колоните и дискретен цветен
 * акцент (hover, сортировка, значки, бледи бутони).
 *
 * Всички стойности, зависещи от accent/isNarrow, са runtime props → inline
 * style; вътрешните antd класове се достигат само през scoped CSS.
 */

const R = {
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  borderMed: "var(--color-shade-3)",
  text: "var(--color-ink)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  danger: "var(--color-danger-ink)",
  dangerDeep: "var(--color-danger-ink)",
};

interface ConfigPanelProps {
  // Уникален CSS клас на панела — на една страница има по няколко панела,
  // всеки със свой accent в scoped CSS-а.
  scope: string;
  accent: PanelAccent;
  icon: React.ReactNode;
  kicker: string;
  title: string;
  count?: number;
  // Дясната част на хедъра: търсачка, чипове, бутон за добавяне.
  toolbar?: React.ReactNode;
  isNarrow?: boolean;
  // При празен списък вложената рамка показва текст вместо таблица.
  isEmpty?: boolean;
  emptyText?: string;
  children: React.ReactNode;
}

export function ConfigPanel({
  scope,
  accent,
  icon,
  kicker,
  title,
  count,
  toolbar,
  isNarrow = false,
  isEmpty = false,
  emptyText,
  children,
}: ConfigPanelProps) {
  return (
    <div
      className={scope}
      style={{
        background: R.surface,
        border: `1px solid ${R.border}`,
        borderRadius: 14,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{panelCss(scope, accent)}</style>

      {/* Header */}
      <div
        style={{
          padding: isNarrow ? "14px 14px 10px" : "14px 22px 12px",
          display: "flex",
          alignItems: isNarrow ? "stretch" : "center",
          flexDirection: isNarrow ? "column" : "row",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div
          className="flex items-center"
          style={{
            gap: 12,
            justifyContent: isNarrow ? "center" : "flex-start",
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: accent.tint,
              color: accent.deep,
              border: `1px solid ${accent.tintBorder}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontSize: 17,
            }}
          >
            {icon}
          </div>
          <div style={{ textAlign: "left" }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                color: R.textMuted,
                letterSpacing: 1.2,
                textTransform: "uppercase",
              }}
            >
              {kicker}
            </div>
            <div className="flex items-center" style={{ gap: 8, marginTop: 2 }}>
              <span
                style={{
                  fontSize: isNarrow ? 15 : 17,
                  fontWeight: 700,
                  color: R.ink,
                  letterSpacing: -0.3,
                  // Заглавието не се чупи на два реда — toolbar-ът се пренася.
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </span>
              {count !== undefined && (
                <span
                  style={{
                    minWidth: 24,
                    height: 22,
                    padding: "0 8px",
                    borderRadius: 999,
                    background: accent.tint,
                    color: accent.deep,
                    border: `1px solid ${accent.tintBorder}`,
                    fontSize: 12,
                    fontWeight: 700,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {count}
                </span>
              )}
            </div>
          </div>
        </div>

        {toolbar && (
          <div
            style={{
              display: "flex",
              alignItems: isNarrow ? "stretch" : "center",
              flexDirection: isNarrow ? "column" : "row",
              gap: 8,
              flexWrap: "wrap",
              justifyContent: isNarrow ? "stretch" : "flex-end",
              width: isNarrow ? "100%" : undefined,
              minWidth: 0,
            }}
          >
            {toolbar}
          </div>
        )}
      </div>

      {/* Таблицата е „вложена": собствена заоблена рамка с отстъп от ръба
          на панела, за да не се слива хедърът ѝ с фона на страницата. */}
      <div
        style={{
          margin: isNarrow ? "0 12px 14px" : "0 20px 20px",
          border: `1px solid ${R.borderMed}`,
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {isEmpty ? (
          <div
            style={{
              padding: 40,
              textAlign: "center",
              color: R.textSoft,
              fontSize: 13,
            }}
          >
            {emptyText}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

// Бледият бутон за добавяне в цвета на секцията (еталонът от Монитори).
export function PanelAddButton({
  onClick,
  label,
  accent,
  isNarrow = false,
  dataAttr,
}: {
  onClick: () => void;
  label: string;
  accent: PanelAccent;
  isNarrow?: boolean;
  dataAttr?: string;
}) {
  const dataProps = dataAttr ? { [dataAttr]: true } : {};
  return (
    <button
      type="button"
      {...dataProps}
      onClick={onClick}
      style={{
        height: 38,
        padding: "0 16px",
        borderRadius: 10,
        border: `1px solid ${accent.tintBorder}`,
        background: accent.tint,
        color: accent.deep,
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: -0.2,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        transition: "background 120ms",
        width: isNarrow ? "100%" : undefined,
        whiteSpace: "nowrap",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = accent.tint2;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = accent.tint;
      }}
    >
      <PlusOutlined style={{ fontSize: 12 }} />
      {label}
    </button>
  );
}

export function PanelIconAction({
  children,
  onClick,
  label,
  danger,
  accent,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  danger?: boolean;
  accent: PanelAccent;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        height: 30,
        width: 30,
        borderRadius: 8,
        background: "transparent",
        border: `1px solid ${R.border}`,
        color: danger ? R.danger : R.textSoft,
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "background 120ms, border-color 120ms, color 120ms",
      }}
      onMouseEnter={(e) => {
        if (danger) {
          e.currentTarget.style.background = "var(--color-danger-tint)";
          e.currentTarget.style.borderColor = "rgba(220,38,38,0.35)";
          e.currentTarget.style.color = R.dangerDeep;
        } else {
          e.currentTarget.style.background = accent.tint;
          e.currentTarget.style.borderColor = accent.tintBorder;
          e.currentTarget.style.color = accent.deep;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.borderColor = R.border;
        e.currentTarget.style.color = danger ? R.danger : R.textSoft;
      }}
    >
      {children}
    </button>
  );
}

// Scoped CSS за вътрешните antd класове (недостижими за Tailwind): компактен
// неутрален хедър на колоните, hover/сортировка в акцента, инпути с мек
// фокус-пръстен в цвета на секцията, пагинация.
function panelCss(scope: string, accent: PanelAccent): string {
  const s = "." + scope;
  return `
    ${s} .ant-table { background: transparent; }
    ${s} .ant-table-thead > tr > th {
      background: var(--color-panel) !important;
      color: ${R.textMuted} !important;
      font-weight: 700 !important;
      font-size: 10px !important;
      letter-spacing: 0.7px !important;
      text-transform: uppercase !important;
      border-bottom: 1px solid ${R.border} !important;
      padding: 7px 12px !important;
      white-space: nowrap !important;
    }
    ${s} .ant-table-thead > tr > th.ant-table-column-has-sorters:hover { background: ${accent.tint} !important; }
    ${s} .ant-table-column-sorter-up.active,
    ${s} .ant-table-column-sorter-down.active { color: ${accent.main} !important; }
    ${s} .ant-table-thead > tr > th::before { display: none !important; }
    ${s} .ant-table-thead > tr > th:first-child,
    ${s} .ant-table-thead > tr > th:last-child { border-radius: 0 !important; }
    ${s} .ant-table-measure-row { display: none !important; }
    ${s} .ant-table-tbody > tr > td {
      border-bottom: 1px solid ${R.border} !important;
      padding: 10px 12px !important;
      font-size: 12.5px;
      color: ${R.text};
      transition: background 120ms;
    }
    ${s} .ant-table-tbody > tr:hover > td { background: ${accent.tint} !important; }
    ${s} .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
    ${s} .ant-input {
      border-radius: 10px !important;
      border-color: ${R.border} !important;
      min-height: 38px !important;
      height: 38px !important;
      padding: 4px 12px !important;
      background: ${R.surface} !important;
      font-size: 13px;
      color: ${R.ink};
    }
    ${s} .ant-input-affix-wrapper {
      border-radius: 10px !important;
      border-color: ${R.border} !important;
      min-height: 38px !important;
      height: 38px !important;
      padding: 4px 11px !important;
      background: ${R.surface} !important;
    }
    ${s} .ant-input-affix-wrapper .ant-input {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      padding: 0 !important;
      height: auto !important;
      min-height: 0 !important;
      font-size: 13px;
      color: ${R.ink};
    }
    ${s} .ant-input-affix-wrapper:focus-within,
    ${s} .ant-input-affix-wrapper:hover,
    ${s} .ant-input:hover,
    ${s} .ant-input:focus {
      border-color: ${accent.main} !important;
      box-shadow: 0 0 0 3px ${accent.focusRing} !important;
    }
    ${s} .ant-input::placeholder,
    ${s} .ant-input-affix-wrapper input::placeholder { color: ${R.textMuted} !important; }
    ${s} .ant-pagination {
      padding: 14px 16px !important;
      margin: 0 !important;
      border-top: 1px solid ${R.border};
      display: flex !important;
      align-items: center !important;
      flex-wrap: wrap;
      gap: 6px;
    }
    ${s} .ant-pagination .ant-pagination-options,
    ${s} .ant-pagination .ant-pagination-total-text,
    ${s} .ant-pagination .ant-pagination-item,
    ${s} .ant-pagination .ant-pagination-prev,
    ${s} .ant-pagination .ant-pagination-next { display: inline-flex !important; align-items: center !important; height: 32px !important; line-height: 30px !important; margin: 0 !important; }
    ${s} .ant-pagination .ant-pagination-options .ant-select .ant-select-selector {
      height: 32px !important;
      min-height: 32px !important;
      padding: 0 11px !important;
      border-radius: 8px !important;
      border-color: ${R.border} !important;
      background: ${R.surface} !important;
      box-shadow: none !important;
    }
    ${s} .ant-pagination .ant-pagination-item {
      border-radius: 8px; border-color: ${R.border}; color: ${R.textSoft};
    }
    ${s} .ant-pagination .ant-pagination-item-active {
      background: ${accent.main} !important; border-color: ${accent.main} !important;
    }
    ${s} .ant-pagination .ant-pagination-item-active a { color: #fff !important; }
    ${s} .ant-pagination .ant-pagination-prev button,
    ${s} .ant-pagination .ant-pagination-next button { border-radius: 8px; border-color: ${R.border}; color: ${R.textSoft}; }
  `;
}
